import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '../components/common/ToastContainer';
import { useAuth } from '../auth/AuthProviderUnified';
import socketService, { PM_EVENTS } from '../services/socketService';
import { NOTIFICATIONS_KEYS } from './useNotifications';
import { HIERARCHY_INBOX_SUMMARY_QUERY_KEY } from '../constants/hierarchyInboxQueryKeys';
import { getMongoUserIdFromAuthUser } from '../utils/authUserId';
import { logger } from '../utils/logger';

const log = logger;

// Maps WebSocket event module prefix → React Query key family for cache invalidation
const PM_QUERY_KEY_MAP = {
  'pm.rfi': ['pm', 'rfis'],
  'pm.submittal': ['pm', 'submittals'],
  'pm.permit': ['pm', 'permits'],
  'pm.punch-item': ['pm', 'punchItems'],
  'pm.change-order': ['pm', 'changeOrders'],
  'pm.meeting': ['pm', 'meetings'],
  'pm.daily-log': ['pm', 'dailyLogs'],
  'pm.pm-document': ['pm', 'documents'],
  'pm.project': ['pm', 'projects'],
};

/**
 * Invalidate the relevant PM React Query cache for the given WebSocket event.
 * E.g., 'pm.rfi.submit' → invalidates ['pm', 'rfis'] queries.
 */
function invalidatePMQueries(queryClient, eventName) {
  const parts = eventName.split('.');
  const modulePrefix = parts.slice(0, 2).join('.');
  const queryKey = PM_QUERY_KEY_MAP[modulePrefix];
  if (queryKey) {
    queryClient.invalidateQueries({ queryKey });
  }
  // Always invalidate notifications inbox for unread badge
  queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });
}

/**
 * Hook that listens for PM real-time events and displays toast notifications.
 * Subscribes to each individual PM event name (exact match) from PM_EVENTS.
 * Mount in a dashboard layout component so toasts appear on any page.
 */
export default function usePMNotifications({ enabled = true } = {}) {
  const queryClient = useQueryClient();
  const { showInfo } = useToast();
  const { user } = useAuth();
  const userId = getMongoUserIdFromAuthUser(user);

  // Own socket lifecycle: disconnect on auth loss; reconnect on user-id change for fresh auth
  useEffect(() => {
    if (!enabled || !userId || !socketService.isRealtimeEnabled()) {
      socketService.disconnect();
      return undefined;
    }

    socketService.connect();

    return () => {
      socketService.disconnect();
    };
  }, [enabled, userId]);

  const handlePMEvent = useCallback((eventName, data) => {
    const title = data.title || 'PM Update';
    const body = data.body || `${data.workflowName || 'Item'} ${data.event || 'updated'}`;
    showInfo(title, body);

    // Invalidate relevant PM queries + notifications inbox
    invalidatePMQueries(queryClient, eventName);
  }, [queryClient, showInfo]);

  // Hierarchy messaging — independent of PM_EVENTS so live receive is never blocked by PM init
  useEffect(() => {
    if (!enabled) return undefined;

    const hierarchyHandler = (data) => {
      const title = data?.subject || 'New message';
      const body = data?.body || 'You have a new team message';
      showInfo(title, body);
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });
      queryClient.invalidateQueries({ queryKey: HIERARCHY_INBOX_SUMMARY_QUERY_KEY });
    };
    socketService.on('hierarchy.message', hierarchyHandler);

    return () => {
      socketService.off('hierarchy.message', hierarchyHandler);
    };
  }, [enabled, queryClient, showInfo]);

  useEffect(() => {
    if (!enabled || !PM_EVENTS.length) return undefined;

    const handlers = [];

    PM_EVENTS.forEach((eventName) => {
      const handler = (data) => handlePMEvent(eventName, data);
      socketService.on(eventName, handler);
      handlers.push({ eventName, handler });
    });

    log.info(`[usePMNotifications] Subscribed to ${PM_EVENTS.length} PM events`);

    return () => {
      handlers.forEach(({ eventName, handler }) => {
        socketService.off(eventName, handler);
      });
    };
  }, [enabled, handlePMEvent]);
}
